import { createFileRoute, isNotFound, Link } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { useTranslation } from '@/lib/i18n';
import { amountTooHighMessage, errorMessages } from '@/lib/portal-messages';
import { Title } from '@worldcoo/ui/components/form/title';
import { Paragraph } from '@worldcoo/ui/components/form/paragraph';
import { useAppForm } from '@/lib/use-app-form';
import { Animate } from '@worldcoo/ui/custom/animate';
import { buttonVariants } from '@worldcoo/ui/components/button';
import { Spinner } from '@worldcoo/ui/components/spinner';
import { usePersistedStore, useEphemeralStore } from '@/lib/app-store';
import { detailsSchema } from '@/lib/details-schema';
import { useEffect } from 'react';
import { usePortalCampaignQuery, usePortalAuthorQuery, usePortalMetadataQuery } from '@/lib/data-hooks';
import { trpc, trpcClient } from '@/lib/trpc';

import { formatList, cn, formatNumber } from '@worldcoo/ui/lib/utils';
import { revalidateLogic } from '@tanstack/react-form';
import { useMutation } from '@tanstack/react-query';
import { generateCaptchaTokenResult } from '@/lib/captcha';
import { applicationType } from '@worldcoo/monolith/shared-context';
import { amountToMinorUnits, hasMinorUnits } from '@worldcoo/shared/currencies';
import {
	DONATION_AMOUNT_ABOVE_LIMIT,
	DONATION_AMOUNT_LIMIT,
	DONOR_FISCAL_DATA_INCOMPLETE,
	localeToLanguage,
} from '@worldcoo/shared/core';
import { toast } from '@/lib/toast';
import { DonateStepPending } from './-components/donate-step-pending';
import { DonorFields } from './-components/donate-details/donor-fields';
import { FiscalFields } from './-components/donate-details/fiscal-fields';
import { DonationTerms } from './-components/donate-details/donation-terms';

export const Route = createFileRoute('/$portalId/$campaignId/donate/details')({
	head: () => ({ meta: [{ name: 'robots', content: 'noindex' }] }),
	component: Component,
	pendingComponent: DonateStepPending,
});

const UNCHARGEABLE_CURRENCY = 'UNCHARGEABLE_CURRENCY';
/** What the preregistration answers when going on to the checkout cannot help. */
const stopsTheDonation = new Set([DONATION_AMOUNT_ABOVE_LIMIT, DONOR_FISCAL_DATA_INCOMPLETE, UNCHARGEABLE_CURRENCY]);

function Component() {
	const { portalId, campaignId } = Route.useParams();
	const navigate = Route.useNavigate();
	const { language } = useTranslation();
	const { t } = useLingui();
	const defaultValues = usePersistedStore((state) => state.details);
	const amount = usePersistedStore((state) => state.amount);
	const hasHydrated = usePersistedStore((state) => state._hasHydrated);
	const setDetails = usePersistedStore((state) => state.setDetails);
	const termsContent = usePersistedStore((state) => state.termsContent);
	const ngosTermsContent = usePersistedStore((state) => state.ngosTermsContent);
	const telefonicaTermsContent = usePersistedStore((state) => state.telefonicaTermsContent);
	const setCachedPreregistration = useEphemeralStore((state) => state.setCachedPreregistration);
	const setCachedPaymentMethods = useEphemeralStore((state) => state.setCachedPaymentMethods);

	const { mutateAsync: preregisterDonation } = useMutation(trpc.payments.preregisterDonation.mutationOptions());

	const campaign = usePortalCampaignQuery(campaignId);
	const metadata = usePortalMetadataQuery();

	// Undefined for a currency the PSP cannot be charged in: the payment server refuses it too, so stop here.
	const campaignCurrency = isNotFound(campaign) ? undefined : campaign.data.currencyCode;
	const currencyCode = campaignCurrency && hasMinorUnits(campaignCurrency) ? campaignCurrency : undefined;
	// The Spanish tax certificate is issued in euros, so a campaign in another currency does not offer it.
	const offersCertificate = campaignCurrency === 'EUR';
	const hideCertificate = (!isNotFound(metadata) && metadata.hideCertificate) || !offersCertificate;

	const author = usePortalAuthorQuery();
	// A Worldcoo Plus portal carries the NGO's brand: no Worldcoo newsletter there (core refuses it as well).
	const offersNewsletter = isNotFound(author) || author.type !== 'ngo';
	const form = useAppForm({
		validators: {
			onChange: detailsSchema(portalId, amount, campaignCurrency, {
				required: t`Required`,
				emailsDoNotMatch: t`Email addresses do not match.`,
				termsAndConditions: t`You must accept the terms and conditions`,
			}),
		},
		validationLogic: revalidateLogic({
			mode: 'submit',
			modeAfterSubmission: 'change',
		}),
		defaultValues: portalId === 'telefonica' ? { ...defaultValues, certificate: true } : defaultValues,
		listeners: {
			onChangeDebounceMs: 300,
			onChange: ({ formApi }) => {
				setDetails(formApi.state.values);
			},
		},
		onSubmit: async ({ value }) => {
			setDetails(value);

			const preregistered = await Promise.all([
				currencyCode ? undefined : Promise.reject(new Error(UNCHARGEABLE_CURRENCY)),
				generateCaptchaTokenResult().then(({ data: captchaToken }) => {
					if (!captchaToken || !amount || !currencyCode) return;

					return preregisterDonation({
						certificated: value.certificate,
						amount: amount,
						campaignId,
						currency: currencyCode,
						applicationId: {
							type: applicationType.portal,
							host: portalId,
						},
						donor: {
							name: value.firstname,
							lastName: value.lastname,
							contact: {
								email: value.email,
								language: localeToLanguage[language],
								physicalAddress: {
									region: value.region,
									street: {
										name: value.streetName,
										number: value.streetNumber,
										type: '',
									},
									additionalInfo: value.additionalInfo,
									city: value.city,
									country: value.country,
									postalCode: value.cp,
									province: value.province,
								},
							},
							document: {
								number: value.documentNumber,
								type: value.documentType,
							},
							subscribeNewsletter: offersNewsletter && value.newsletter,
						},
						checkbox: [
							termsContent && { type: 'termsAndConditions' as const, ...termsContent },
							ngosTermsContent && { type: 'ngoTermsAndConditions' as const, ...ngosTermsContent },
							telefonicaTermsContent && {
								type: 'telefonicaTermsAndConditions' as const,
								...telefonicaTermsContent,
							},
						].filter((v) => v !== undefined),
						captchaToken,
					}).then((result) => {
						if (result?.captchaPassed) {
							setCachedPreregistration(result);
						}
					});
				}),
				generateCaptchaTokenResult().then(({ data: captchaToken }) => {
					if (!captchaToken || !amount || !currencyCode) return;

					return trpcClient.payments.getPaymentMethods
						.mutate({
							amount: { value: amountToMinorUnits(amount, currencyCode), currency: currencyCode },
							countryCode: value.country,
							locale: language,
							captchaToken,
						})
						.then((paymentMethods) => {
							if (paymentMethods.captchaPassed) {
								setCachedPaymentMethods(paymentMethods);
							}
						});
				}),
			]).then(
				() => true,
				(error: unknown) => {
					console.error('Background preregistration failed:', error);
					const code = error instanceof Error ? error.message : undefined;
					if (code === DONATION_AMOUNT_ABOVE_LIMIT) {
						toast.error(t(amountTooHighMessage(formatNumber(language, DONATION_AMOUNT_LIMIT, 0))));
					} else {
						toast.error(t(errorMessages.internal));
					}
					// Refused by core or uncharged in this currency, the checkout would only fail again: stay here.
					return !code || !stopsTheDonation.has(code);
				},
			);
			if (!preregistered) return;

			await navigate({ to: `/$portalId/$campaignId/donate/checkout`, params: { portalId, campaignId } });
		},
	});

	useEffect(() => {
		if (hasHydrated && !amount) navigate({ to: `/$portalId/$campaignId/donate`, params: { portalId, campaignId } });
	}, [amount, navigate, campaignId, hasHydrated, portalId]);

	// Hidden is not enough: a certificate switched on earlier (Telefónica starts with it on) would still be asked for.
	useEffect(() => {
		if (!offersCertificate && form.getFieldValue('certificate')) form.setFieldValue('certificate', false);
	}, [offersCertificate, form]);

	if (isNotFound(campaign) || isNotFound(author)) return null;

	const ngoNames = formatList(
		language,
		campaign.data.ngos.map((ngo) => ngo.name ?? ''),
	);

	return (
		<Animate>
			<form
				onSubmit={(event) => {
					event.preventDefault();
					form.handleSubmit();
				}}
				className="flex flex-col gap-6"
			>
				<div className="flex flex-col gap-2">
					<Title size="lg" className="text-center">
						{t`Add your details to proceed`}
					</Title>
					<Paragraph className="text-center">
						{/* Offers the certificate only where its switch is shown (the same rule as `FiscalFields`). */}
						{language === 'it' || hideCertificate
							? t`Fill in your details to complete your donation.`
							: t`Fill in your details to complete your donation. You can also request your tax certificate here.`}
					</Paragraph>
				</div>
				<DonorFields form={form} />
				<FiscalFields
					form={form}
					portalId={portalId}
					amount={amount}
					currencyCode={campaignCurrency}
					hideCertificate={hideCertificate}
				/>
				<DonationTerms form={form} portalId={portalId} ngoNames={ngoNames} authorName={author.name ?? ''} />
				{offersNewsletter && (
					<form.AppField
						name="newsletter"
						children={(field) => (
							<field.CheckboxField
								className="gap-3"
								label={
									<span className="text-dark-alt/80 font-normal -tracking-[0.2px]">{t`Subscribe to stay up to date with the causes we support, the projects we fund, and the people we empower around the world.`}</span>
								}
							/>
						)}
					/>
				)}
				<form.Subscribe
					selector={(state) => state.isSubmitting}
					children={(isSubmitting) => (
						<Link
							to="/$portalId/$campaignId/donate/checkout"
							params={{ portalId, campaignId }}
							className={cn(
								buttonVariants({ size: '2xl' }),
								'mx-auto mt-6 w-full min-w-fit lg:w-60',
								isSubmitting && 'pointer-events-none opacity-50',
							)}
							aria-disabled={isSubmitting}
							onClick={(e) => {
								// The Link is only so the router preloads the checkout head.
								e.preventDefault();
								form.handleSubmit();
							}}
						>
							{isSubmitting && <Spinner />}
							{t`Next`}
						</Link>
					)}
				/>
			</form>
		</Animate>
	);
}
